import { API_BASE_URL } from './constants';

export interface ImageOptions {
  width?: number;
  height?: number;
  format?: 'jpg' | 'jpeg' | 'png' | 'webp' | 'gif';
  compression?: number; // 0-100
  crop?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7; // 0=Center, 5=Keep aspect ratio, 6=Fit image, 7=CenterOrFocal
  forceDownload?: boolean;
  resolution?: number; // 72-300
  fillCanvas?: boolean;
  background?: string; // Hex code for PNG background
}

/**
 * Generates an optimized image URL using DynamicWeb's GetImage.ashx handler
 * @param imagePath - The relative path to the image (e.g., '/Files/Digital assets/Employees/tyler.jpg')
 * @param options - Image optimization options
 * @returns Optimized image URL
 */
export function getOptimizedImageUrl(imagePath: string, options: ImageOptions = {}): string {
  // If no path provided, return empty string
  if (!imagePath) return '';
  
  // Remove leading slash if present for consistency
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  // Build query parameters
  const params = new URLSearchParams();
  params.append('Image', cleanPath);
  
  if (options.width) params.append('Width', options.width.toString());
  if (options.height) params.append('Height', options.height.toString());
  if (options.format) params.append('Format', options.format);
  if (options.compression !== undefined) params.append('Compression', options.compression.toString());
  if (options.crop !== undefined) params.append('Crop', options.crop.toString());
  if (options.forceDownload) params.append('ForceDownload', '1');
  if (options.resolution) params.append('Resolution', options.resolution.toString());
  if (options.fillCanvas) params.append('FillCanvas', '1');
  if (options.background) params.append('Background', options.background);
  
  return `${API_BASE_URL}/Admin/Public/GetImage.ashx?${params.toString()}`;
}

/**
 * Preset for thumbnail images in grid views
 */
export function getThumbnailUrl(imagePath: string): string {
  return getOptimizedImageUrl(imagePath, {
    width: 400,
    height: 300,
    format: 'webp',
    compression: 75,
    crop: 5, // Keep aspect ratio
  });
}

/**
 * Preset for PDF thumbnail - generates preview of first page
 */
export function getPdfThumbnailUrl(imagePath: string): string {
  return getOptimizedImageUrl(imagePath, {
    width: 400,
    height: 520,
    format: 'png',
    compression: 80,
    crop: 6, // Fit image (important for PDFs to show full page)
  });
}

/**
 * Preset for small icons/avatars
 */
export function getIconUrl(imagePath: string, size: number = 64): string {
  return getOptimizedImageUrl(imagePath, {
    width: size,
    height: size,
    format: 'webp',
    compression: 75,
    crop: 0, // Center crop
  });
}

/**
 * Preset for product images in lists/carts
 */
export function getProductImageUrl(imagePath: string): string {
  return getOptimizedImageUrl(imagePath, {
    width: 300,
    height: 300,
    format: 'webp',
    compression: 75,
    crop: 5, // Keep aspect ratio
  });
}

/**
 * Preset for full-size download
 */
export function getDownloadUrl(imagePath: string): string {
  return getOptimizedImageUrl(imagePath, {
    compression: 85,
    forceDownload: true,
  });
}

/**
 * Preset for hero/banner images
 */
export function getHeroImageUrl(imagePath: string, width: number = 1200, height: number = 600): string {
  return getOptimizedImageUrl(imagePath, {
    width,
    height,
    format: 'webp',
    compression: 80,
    crop: 0, // Center crop
  });
}
